import React, { useState, useEffect, useRef, useCallback } from "react";
import { LookupInput, type LookupInputOption, type LookupInputState } from "@/components/ui/LookupInput";
import { shiftsService } from "@/services/shiftService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

interface ShiftInputProps {
  value: string | null;
  projectId?: string | null;
  state?: LookupInputState;
  label?: string;
  error?: string;
  optionsSource?: LookupInputOption[];
  placeholder?: string;
  onChange?: (shiftId: string | null) => void;
}

/** Format shift date/time for display */
function formatShiftDateTime(startTime: string | Date | null | undefined): string {
  if (!startTime) return "";
  const date = typeof startTime === "string" ? new Date(startTime) : startTime;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Get status badge tone */
function getStatusTone(status: string | undefined): "emerald" | "amber" | "red" | "slate" {
  switch (status) {
    case "COMPLETE":
      return "emerald";
    case "APPROVED":
    case "ADVANCED":
      return "emerald";
    case "PENDING":
      return "amber";
    case "CANCELED":
    case "CANCELED - BILLABLE":
      return "red";
    default:
      return "slate";
  }
}

export function ShiftInput({
  value,
  projectId,
  state = "editing",
  label = "Shift",
  error,
  optionsSource,
  placeholder = "Select shift…",
  onChange,
}: ShiftInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const handleIdlePress = useCallback(() => {
    if (!value) return;
    const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
    const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
    if (currentPanel && currentId) {
      pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
    }
    navigateToEntity("shift", value);
  }, [value, currentParams, pushNavigation, currentEntity, navigateToEntity]);

  const [options, setOptions] = useState<LookupInputOption[]>([]);
  const [loading, setLoading] = useState(false);
  const prevProjectId = useRef(projectId);

  // Auto-clear value when projectId changes
  useEffect(() => {
    if (prevProjectId.current !== projectId && prevProjectId.current !== undefined) {
      onChange?.(null);
    }
    prevProjectId.current = projectId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Only load shifts when a project is selected
  useEffect(() => {
    if (optionsSource) {
      setOptions(optionsSource);
      return;
    }

    if (!projectId) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const shifts = await shiftsService.getShifts({ limit: 500 });
        if (cancelled) return;

        const filtered = shifts.filter((s) => s.project?.id === projectId);

        setOptions(
          filtered.map((s) => ({
            id: s.id!,
            name: s.name || `Shift ${formatShiftDateTime(s.start_time)}`,
            subtitle: formatShiftDateTime(s.start_time),
            icon: "calendar-clock" as const,
            badge: s.status
              ? {
                  text: s.status,
                  tone: getStatusTone(s.status),
                }
              : undefined,
          })),
        );
      } catch (err) {
        console.error("Failed to load shifts:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, optionsSource]);

  // Disable when no project selected
  const isDisabled = !projectId;
  const resolvedState: LookupInputState = isDisabled ? "idle" : loading ? "loading" : state;

  const resolvedPlaceholder = isDisabled ? "Select a project first" : placeholder;

  return (
    <LookupInput
      label={label}
      value={value}
      options={options}
      state={resolvedState}
      icon="calendar-clock"
      error={error}
      placeholder={resolvedPlaceholder}
      onChange={isDisabled ? undefined : onChange}
      onIdlePress={handleIdlePress}
    />
  );
}
